import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Login from './components/atoms/Login';
import Pages from './Pages/index';
import Layout from './components/Layout';
import { lazy, Suspense } from 'react';
import Signup from './components/atoms/SignUp';
import RouteGuard from './components/RouteGuard';
import CampaignDetailSkeleton from './components/atoms/Campaigns/CampaignDetailSkeleton';
import OffersSkeleton from './components/atoms/Offers/OffersSkeleton';
import OfferDetailSkeleton from './components/atoms/Offers/OfferDetailSkeleton';

const SignupAlias = () => <Navigate to={`/sign-up${useLocation().search}`} replace />;

/*
 * Every screen below loads on demand, the first time its route is opened.
 * Imported directly, they all went into the bundle the app downloads and runs
 * at login, with every icon set they use, before the first page could show.
 * The shell (Layout, RouteGuard, the main pages loader) and the sign-in and
 * sign-up screens stay in it, so the first screen still appears at once.
 */
const ForgotPassword = lazy(() => import('./components/atoms/ForgotPassword'));
const AgentDetail = lazy(() => import('./components/atoms/AgentDetail'));
const VerifyOtp = lazy(() => import('./Pages/verify-otp'));
const Paywall = lazy(() => import('./Pages/paywall'));
const ConnectLinkedin = lazy(() => import('./Pages/connect-linkedin'));
const AiLearning = lazy(() => import('./Pages/ai-learning'));
const CreateCompaign = lazy(() => import('./components/atoms/CreateCompaign'));
const ChangePassword = lazy(() => import('./components/atoms/ForgotPassword/ChangePassword'));
const Successful = lazy(() => import('./components/atoms/ForgotPassword/Successful'));
const OtpConfirm = lazy(() => import('./components/atoms/ForgotPassword/OtpConfirm'));
const DeleteDomain = lazy(() => import('./components/atoms/DeleteDomain'));
const TasksPreviewSection = lazy(() => import('./components/atoms/TasksPreviewSection'));
const DeleteDomainEmail = lazy(() => import('./components/atoms/DeleteDomainEmail'));
const CampaignLaunched = lazy(() => import('./components/atoms/CreateCompaign/CampaignLaunched'));
const FindLeads = lazy(() => import('./components/atoms/FindLeads'));
const Billing = lazy(() => import('./components/atoms/Billing'));
const DeleteCampaign = lazy(() => import('./components/atoms/CompaignDetail/DeleteCampaign'));
const AddMoreLeads = lazy(() => import('./components/atoms/CompaignDetail/AddMoreLeads'));
const UnsubscribeCampaign = lazy(() => import('./Pages/unsubscribe-campaign'));
const DeleteTask = lazy(() => import('./components/atoms/Tasks/DeleteTask'));
const LeadPreview = lazy(() => import('./Pages/lead-preview'));
const SingleListLead = lazy(() => import('./Pages/single-list-lead'));
const SingleImportJob = lazy(() => import('./Pages/single-job-lead'));
const AcceptWorkspaceInvitePage = lazy(() => import('./Pages/accept-workspace-invite'));
const FlowBuilder = lazy(() => import('./components/automation/FlowBuilder'));
const QuickAction = lazy(() => import('./Pages/quick-action'));
const BulkCsvUpload = lazy(() => import('./components/atoms/BulkCsvUpload'));
const ConnectEmailModal = lazy(() => import('./components/atoms/ConnectEmailDomainModal/ConnectEmailModal'));
const CreateOffer = lazy(() => import('./Pages/create-offer'));
const OfferDetail = lazy(() => import('./Pages/offer-detail'));
const OffersPage = lazy(() => import('./Pages/offers'));
const CampaignDetailPage = lazy(() => import('./Pages/campaign-detail'));
const McpConsentPage = lazy(() => import('./Pages/mcp-consent'));

const Router = () => {
  return (
    // Catches any route without a boundary of its own while its screen loads.
    <Suspense fallback={null}>
      <Routes>
        {/* Public Routes - No authentication required */}
        <Route
          path="/"
          element={
            <RouteGuard requireAuth={false}>
              <Login />
            </RouteGuard>
          }
        />

        <Route
          path="/sign-up"
          element={
            <RouteGuard requireAuth={false}>
              <Signup />
            </RouteGuard>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <RouteGuard requireAuth={false}>
              <ForgotPassword />
            </RouteGuard>
          }
        />

        <Route
          path="/verify-otp"
          element={
            <RouteGuard requireAuth={false}>
              <VerifyOtp />
            </RouteGuard>
          }
        />

        {/* The landing page links to /signup?plan=… */}
        <Route path="/signup" element={<SignupAlias />} />

        {/* Signup step 3: the card for the 7-day trial — full page, outside Layout like the other auth steps. */}
        <Route
          path="/paywall"
          element={
            <RouteGuard requireAuth={true}>
              <Paywall />
            </RouteGuard>
          }
        />

        {/* Signup step 4 — full page, outside Layout like the other auth steps. */}
        <Route
          path="/connect-linkedin"
          element={
            <RouteGuard requireAuth={true}>
              <ConnectLinkedin />
            </RouteGuard>
          }
        />

        {/* "Your AI is learning" — after step 3, before AI Home. */}
        <Route
          path="/ai-learning"
          element={
            <RouteGuard requireAuth={true}>
              <AiLearning />
            </RouteGuard>
          }
        />

        <Route
          path="/unsubscribe-campaign/:id"
          element={
            <RouteGuard requireAuth={false}>
              <UnsubscribeCampaign />
            </RouteGuard>
          }
        />

        {/*
        The OAuth consent screen an AI client is redirected to.

        Signed in but deliberately outside Layout: the user arrives here from
        another application, mid-flow, and the only thing on screen should be the
        decision being asked. Note that RouteGuard sends an unauthenticated
        visitor to the login page without keeping this URL, so someone who is
        signed out has to start the connection again from their client.
      */}
        <Route
          path="/mcp-consent"
          element={
            <RouteGuard requireAuth={true}>
              <Suspense fallback={null}>
                <McpConsentPage />
              </Suspense>
            </RouteGuard>
          }
        />

        <Route
          path="/accept-workspace-invite"
          element={
            <RouteGuard requireAuth={false}>
              <AcceptWorkspaceInvitePage />
            </RouteGuard>
          }
        />

        {/* Parked — /billing

          Nothing links here, and Stripe checkout returns to /dashboard, not to this.

      <Route
        path="/billing"
        element={
          <RouteGuard requireAuth={true}>
            <Billing />
          </RouteGuard>
        }
      />
      */}

        <Route
          path="/verify-email"
          element={
            <RouteGuard requireAuth={false}>
              <OtpConfirm />
            </RouteGuard>
          }
        />

        <Route
          path="/change-password"
          element={
            <RouteGuard requireAuth={false}>
              <ChangePassword />
            </RouteGuard>
          }
        />

        <Route
          path="/successful"
          element={
            <RouteGuard requireAuth={true}>
              <Successful />
            </RouteGuard>
          }
        />

        {/* Automation Routes - Must be before /:view to prevent matching */}
        {/* The builder is a full-screen takeover in the Remix reference
          (`fixed inset-0 z-50`), so it renders without the app chrome. */}
        <Route
          path="/automation/flow-builder"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={false}>
                <Suspense fallback={null}>
                  <FlowBuilder />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* CSV Bulk Upload — restricted to ghous@webevis.cm inside component */}
        {/* Parked — /csv-upload

          Never linked. RouteGuard only tests the path; the component itself is restricted to one address.

      <Route
        path="/csv-upload"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <BulkCsvUpload />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        <Route
          path="/campaign-manager/:id"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                {/* The page's own skeleton while its code downloads, so it never opens blank. */}
                <Suspense fallback={<CampaignDetailSkeleton />}>
                  <CampaignDetailPage />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* Protected Routes - Authentication required */}
        <Route
          path="/:view"
          element={
            <RouteGuard requireAuth={true}>
              <Pages />
            </RouteGuard>
          }
        />

        {/* Parked — /dashboard/agents/:id

          Reached only from Workforce, which is commented out of the navbar. Restore both together.

      <Route
        path="/dashboard/agents/:id"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <AgentDetail />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        {/* Parked — /lead-preview/:id

          Contacts already had it behind LEAD_PREVIEW_ENABLED=false; the Unibox lead card is parked with it.

      <Route
        path="/lead-preview/:id"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <LeadPreview />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}
        {/* Parked — /single-list-lead/:id

          Reached only from the /leads-list screen, which is not in the navbar.

      <Route
        path="/single-list-lead/:id"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <SingleListLead />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}
        {/* Parked — /single-job-lead/:id

          Reached only from the /import-jobs screen, which is not in the navbar.

      <Route
        path="/single-job-lead/:id"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <SingleImportJob />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}
        {/* Parked — /tasks-preview

          Nothing in the app links here.

      <Route
        path="/tasks-preview"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <TasksPreviewSection />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}
        {/* Parked — /find-leads

          Never linked. One component only compares the current path against it.

      <Route
        path="/find-leads"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <FindLeads />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        {/* Parked — /delete-task

          Nothing in the app links here.

      <Route
        path="/delete-task"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <DeleteTask />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        <Route
          path="/delete-domain"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={null}>
                  <DeleteDomain />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* Parked — /delete-email

          Nothing in the app links here.

      <Route
        path="/delete-email"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <DeleteDomainEmail />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}
        <Route
          path="/get-started"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={null}>
                  <QuickAction />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        <Route
          path="/create-campaign"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={null}>
                  <CreateCompaign />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        <Route
          path="/create-offer"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={null}>
                  <CreateOffer />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* Offers moved out of the /profile tab strip into a page of its own,
          reached from the profile menu — same as the Remix reference. */}
        <Route
          path="/offers"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={<OffersSkeleton />}>
                  <OffersPage />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        <Route
          path="/offers/:id"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={<OfferDetailSkeleton />}>
                  <OfferDetail />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        <Route
          path="/connect-email-account"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={false}>
                <Suspense fallback={null}>
                  <ConnectEmailModal />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* Parked — /add-more-campaign

          Nothing in the app links here.

      <Route
        path="/add-more-campaign"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <AddMoreLeads />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        {/* Parked — /delete-campaign

          Nothing in the app links here.

      <Route
        path="/delete-campaign"
        element={
          <RouteGuard requireAuth={true}>
            <Layout showTemplate={true}>
              <Suspense fallback={null}>
                <DeleteCampaign />
              </Suspense>
            </Layout>
          </RouteGuard>
        }
      />
      */}

        <Route
          path="/campaign-launched"
          element={
            <RouteGuard requireAuth={true}>
              <Layout showTemplate={true}>
                <Suspense fallback={null}>
                  <CampaignLaunched />
                </Suspense>
              </Layout>
            </RouteGuard>
          }
        />

        {/* Catch all route - redirect to login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
